import { EMPTY_VALUE } from '../utils/formatCurrency';
import { usePaymentTypesDropdown } from './usePaymentTypesDropdown';

/**
 * Resolve `IndividualPaymentHeaderViewModel.paymentType` (um `number`) para o
 * rótulo localizado do método de pagamento.
 *
 * O header não devolve o texto, só o id. O único dicionário disponível é o
 * lookup `GET /individualpayment/shared/payment-types`, o mesmo que alimenta o
 * filtro da listagem — daí o reuso de `usePaymentTypesDropdown`.
 *
 * Quando o id não casa com nenhuma opção (ou o lookup ainda não carregou), o
 * retorno é o marcador de ausência: exibir um rótulo adivinhado seria pior que
 * admitir que não sabemos.
 *
 * @param paymentType - Id do método de pagamento vindo do header.
 * @returns `{ label, isLoading }`.
 *
 * @example
 * ```tsx
 * const { label } = usePaymentTypeLabel(header?.paymentType);
 * ```
 */
export function usePaymentTypeLabel(
  paymentType: number | null | undefined,
  enabled = true,
): {
  readonly label: string;
  readonly isLoading: boolean;
} {
  const { paymentTypes, isLoading } = usePaymentTypesDropdown(enabled);

  if (paymentType === null || paymentType === undefined) {
    return { label: EMPTY_VALUE, isLoading } as const;
  }

  const match = paymentTypes.find(option => String(option.id) === String(paymentType));

  return { label: match?.label ?? EMPTY_VALUE, isLoading } as const;
}
